/* eslint-disable no-param-reassign */
import axios, { CancelToken, CancelTokenSource } from 'axios';
import { getCookie } from 'utils/cookie';
import { buildRequestContext, RequestContext } from 'utils/datadogRequestCapture';
import { isExpiredSession } from 'utils/error';

export const apiBaseUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

export type ResponseType = 'json' | 'text' | 'blob' | 'arraybuffer';

/**
 * Generic fallback the backend sends as `displayMessage` when it couldn't derive a meaningful,
 * user-facing message. Must stay in sync with GENERIC_ERROR_MESSAGE in the backend's
 * GlobalDefaultExceptionHandler (and the ApiErrorResponse default). When the backend falls back to
 * this string but also provides a more specific `message`, we surface that specific `message` instead.
 */
export const GENERIC_BACKEND_ERROR_MESSAGE = 'An unknown error has occurred. Please contact Thera!';

if (!apiBaseUrl) {
  console.error("NEXT_PUBLIC_BACKEND_URL is not defined, app won't work!");
}

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: false,
  transitional: {
    silentJSONParsing: false,
  },
});

export const axiosSourceMap: Map<string, CancelTokenSource[]> = new Map();

/**
 * stop ongoing api calls,
 * cancel all ongoing axios requests
 */
export const cancelAxiosRequests = () => {
  try {
    axiosSourceMap.forEach((sources) => {
      if (Array.isArray(sources)) {
        sources.forEach((source) => source.cancel('UnAuthorized'));
      }
    });
    axiosSourceMap.clear(); // Clear the map
  } catch (error) {
    console.error('Error while canceling axios requests', error);
  }
};

// Generate a unique CancelToken and store its source in axiosSourceMap
const generateCancelToken = (key: string): CancelToken => {
  const source = axios.CancelToken.source();

  // If there are already sources associated with this key, add this one
  if (axiosSourceMap.has(key)) {
    axiosSourceMap.get(key).push(source);
  } else {
    // Else create a new array with this source
    axiosSourceMap.set(key, [source]);
  }

  return source.token;
};

// Function to remove a source from axiosSourceMap
function removeSource(key: string, source: CancelToken) {
  if (axiosSourceMap.has(key)) {
    const sources = axiosSourceMap.get(key);
    const index = sources.findIndex((s) => s.token === source);

    if (index !== -1) {
      sources.splice(index, 1);
    }

    if (sources.length === 0) {
      axiosSourceMap.delete(key);
    } else {
      axiosSourceMap.set(key, sources);
    }
  }
}

class CustomError extends Error {
  httpStatus: number;
  isAxiosError: boolean;
  code: string;
  requestContext?: RequestContext;
  /** Preserve original error for Datadog debugging */
  originalError?: unknown;
  /** Standard Error.cause (supported in modern runtimes) */
  cause?: unknown;

  constructor(message, status = 500, isAxiosError = false, code = 'ERR_UNKNOWN', originalError?: unknown) {
    super(message);
    // Explicitly name the error, but allow an original error to override for better Datadog grouping.
    this.name = 'CustomError';
    this.httpStatus = status;
    this.isAxiosError = isAxiosError;
    this.code = code;

    /**
     * Preserve the original error name + stack trace so Datadog Error Tracking
     * doesn't get "clouded" by every failure looking like CustomError.
     */
    try {
      const originalAny = originalError as any;
      if (originalAny?.name) this.name = originalAny.name;
      if (originalAny?.stack) this.stack = originalAny.stack;
      this.originalError = originalError;
      this.cause = originalError;
      // If our code is generic but the original has a more specific code, keep it
      if (this.code === 'ERR_UNKNOWN' && originalAny?.code) {
        this.code = originalAny.code;
      }
    } catch {
      // no-op: never let error instrumentation break error handling
    }
  }
}

const errorHandler = (error) => {
  const response = error?.response;
  const isAxiosError = error?.isAxiosError;
  const status = response?.status || error?.status || 500;
  const requestContext = buildRequestContext(error);
  let errorDataObject = {};

  const rejectWithContext = (customError: CustomError) => {
    customError.requestContext = requestContext;
    return Promise.reject(customError);
  };

  if (error.code === 'ECONNABORTED' && error?.message.includes('timeout')) {
    console.warn('Request timed out');
    return rejectWithContext(new CustomError('Request timed out', 408, true, 'ERR_TIMEOUT', error));
  }

  if (error.code === 'ERR_CONNECTION_REFUSED') {
    console.warn('Connection refused - possible network blocking');
    return rejectWithContext(
      new CustomError('Connection refused - possible network blocking', 502, true, 'ERR_CONNECTION_REFUSED', error),
    );
  }

  if (error.code === 'ERR_INTERNET_DISCONNECTED' || error.code === 'ERR_NETWORK') {
    console.warn('Network Error');
    return rejectWithContext(new CustomError('No Internet Connection', 502, true, 'INTERNET_DISCONNECTED', error));
  }

  if (axios.isCancel(error)) {
    return rejectWithContext(new CustomError('Request_Cancelled', 499, true, 'ERR_CANCELED', error));
  }

  let message = error?.message || 'Server Error! Please try again later.';
  let customError;

  if (!response) {
    // Network error or other issue where response is not available
    return rejectWithContext(new CustomError(message, 500, isAxiosError, 'ERR_UNKNOWN', error));
  }

  if (response.data && typeof response.data === 'object') {
    // Handle JSON response
    if (response?.data?.error) {
      errorDataObject = response?.data?.error;
    } else {
      errorDataObject = response?.data;
    }
    message = response?.data?.message || message;
    // Check for specific authentication-related errors
    if (isExpiredSession(status, response?.data)) {
      // The backend rejected this session, so clear the stale JWT cookie to keep Stytch's
      // client-side state in sync. Without this, the app keeps firing authenticated API calls
      // against a dead session (e.g. on /sign-in page).
      if (typeof document !== 'undefined') {
        document.cookie = 'stytch_session_jwt=; Max-Age=0; path=/; SameSite=Lax';
      }
      customError = new CustomError(message, status, isAxiosError, 'ERR_AUTHENTICATION', error);
    } else {
      customError = new CustomError(message, status, isAxiosError, 'ERR_UNKNOWN', error);
    }
  } else {
    // Non-JSON response, such as a 5xx error page
    customError = new CustomError('Something went wrong!', status, isAxiosError, 'ERR_UNKNOWN', error);
  }

  customError.requestContext = requestContext;
  // Copy server response data to the custom error object
  Object.assign(customError, errorDataObject);
  // Decide which server-provided message to surface to the user.
  // `displayMessage` is the backend's intended user-facing copy, so we prefer it — EXCEPT when it's
  // just the generic fallback while a more specific `message` is available. In that case we show the
  // specific `message` instead. Restricted to client (4xx) errors so we don't leak internal 5xx details.
  const backendMessage = response?.data?.message ?? response?.data?.error?.message;
  const backendDisplayMessage = response?.data?.displayMessage ?? response?.data?.error?.displayMessage;
  if (backendDisplayMessage) {
    const displayMessageIsGeneric = backendDisplayMessage === GENERIC_BACKEND_ERROR_MESSAGE;
    const hasSpecificMessage = Boolean(backendMessage) && backendMessage !== backendDisplayMessage;
    const isClientError = status >= 400 && status < 500;
    customError.message =
      displayMessageIsGeneric && hasSpecificMessage && isClientError ? backendMessage : backendDisplayMessage;
  }
  return Promise.reject(customError);
};

const successHandler = (response) => {
  const resContentType = response?.headers?.['content-type'];
  // If the response is a blob/pdf/arraybuffer/stream, return it as-is; arraybuffer means the caller
  // explicitly requested raw binary data, so headers must be preserved regardless of content-type.
  if (
    response?.config?.responseType === 'arraybuffer' ||
    resContentType?.includes('application/octet-stream') ||
    resContentType?.includes('application/pdf')
  ) {
    return response;
  }
  const status = response?.status || 200;
  const rawResponse = response?.data;

  /**
   * An empty body (204, or a zero-length 200) resolves to `null` rather than to a fabricated
   * `{ httpStatus, status_code }` object. That object was indistinguishable from a real payload, so
   * "the server sent nothing" reached consumers as a truthy non-array and the ubiquitous
   * `data || []` / `if (!data)` idioms let it through into `.map()` — "x.map is not a function",
   * tripping the React error boundary. `null` is falsy, so those idioms are correct by default.
   */
  if (rawResponse === undefined || rawResponse === null || rawResponse === '') {
    return null;
  }

  /**
   * An HTML body where JSON was expected is a protocol violation, not data — a proxy/captive-portal
   * interstitial or a gateway error page served with a 200. axios can't reject it for us: `callApi`
   * sets no `responseType`, so `strictJSONParsing` is false and the failed `JSON.parse` is swallowed,
   * handing the raw markup to the caller, which then crashes on `.map()`. Rejecting routes it through
   * the normal error path so callers surface an error state instead.
   *
   * Deliberately keyed on HTML rather than "anything that isn't JSON": plenty of endpoints answer with
   * a bare `ResponseEntity<String>`, which Spring serves as text/plain (`/stripe/login-link`,
   * `/user/reset-mfa`), and those are legitimate string payloads. The content-type sniff is backed up
   * by a leading-'<' check for proxies that omit the header entirely.
   */
  const expectsJson = !response?.config?.responseType || response?.config?.responseType === 'json';
  const looksLikeHtml =
    resContentType?.includes('text/html') ||
    resContentType?.includes('application/xhtml+xml') ||
    (!resContentType && rawResponse?.trimStart?.().startsWith('<'));
  if (typeof rawResponse === 'string' && expectsJson && looksLikeHtml) {
    return Promise.reject(
      new CustomError(
        `Expected JSON but received ${resContentType || 'an unknown content type'}`,
        502,
        false,
        'ERR_NON_JSON_RESPONSE',
      ),
    );
  }

  const actualResponse = rawResponse;
  if (
    // if the response is not a string, number, boolean or array, i.e. it is an object
    typeof actualResponse !== 'string' &&
    typeof actualResponse !== 'number' &&
    typeof actualResponse !== 'boolean' &&
    !Array.isArray(actualResponse)
  ) {
    actualResponse.httpStatus = status;
    actualResponse.status_code = status;
  }
  return actualResponse;
};

// on successful response
axiosInstance.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error),
);

const controllers = {};

// Promise<AxiosResponse<any, any>>
export async function callApi(
  url: string,
  method: string,
  params: any,
  isCheckPDFStream?: boolean,
  theraCompanyID?: string,
  isCancelable?: boolean,
  timeoutMs?: number,
): Promise<any> {
  method = method.toUpperCase();
  url =
    method !== 'GET' ? url : params && Object.keys(params)?.length > 0 ? `${url}?${new URLSearchParams(params)}` : url;
  const jwt = getCookie('stytch_session_jwt');
  if (!jwt) {
    return Promise.reject(new CustomError('UnAuthorized', 401, false, 'ERR_NO_SESSION'));
  }
  let key = `${method}-${url}-${JSON.stringify(params)}`;
  const cancelToken = generateCancelToken(key);

  if (isCancelable) {
    key = `${method}-${url}`;
    // abort the previous request
    if (controllers[key]) {
      controllers[key].abort();
    }

    // Create a new AbortController
    const controller = new AbortController();
    controllers[key] = controller;
  }

  axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + jwt;

  const config = {
    url,
    method: method,
    data: params,
    cancelToken,
    signal: isCancelable ? controllers[key]?.signal : undefined,
    ...(timeoutMs && { timeout: timeoutMs }),
  };

  if (isCheckPDFStream) {
    config['headers'] = { Accept: 'application/pdf' };
    config['responseType'] = 'arraybuffer';
  }
  if (theraCompanyID) {
    config['headers'] = { ...config?.['headers'], 'Thera-Company-Id': theraCompanyID };
  }

  /* Note: Axios handles data parsing automatically based on the response content-type header */
  return axiosInstance(config).finally(() => {
    // remove the cancel token source when the request is completed
    removeSource(key, cancelToken);
  });
}

export async function uploadFile(url: string, method: string, file: File, params: any = {}): Promise<any> {
  const jwt = getCookie('stytch_session_jwt');
  axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + jwt;

  const key = `${method}-${url}-${JSON.stringify(params)}`;
  const cancelToken = generateCancelToken(key);
  const formData = new FormData();
  formData.append('file', file, file.name);
  for (const paramsKey in params) {
    formData.append(paramsKey, params[paramsKey]);
  }

  // Set the 'Content-Type' header for the instance to 'multipart/form-data'
  return axiosInstance({
    url,
    method: method.toUpperCase(),
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    cancelToken,
  }).finally(() => {
    // remove the cancel token source when the request is completed
    removeSource(key, cancelToken);
  });
}

export interface CheckApiResponse {
  results?: any;
  error?: any;
  url?: string;
  headers?: Record<string, string>;
  success?: true | false;
  data?: any;
}

const checkApiInstanceBackend = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CHECKHQ_API_URL,
  headers: {
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_CHECKHQ_API_KEY}`,
  },
});

const checkApiInstanceBackendForDemoCompany = axios.create({
  baseURL: 'https://sandbox.checkhq.com',
  headers: {
    Authorization: `Bearer ${process.env.NEXT_PUBLIC_CHECKHQ_API_KEY_DEMO}`,
  },
});

const checkApiInstanceFrontend = axios.create({
  baseURL: '/api/check',
});

checkApiInstanceFrontend.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error),
);

checkApiInstanceBackend.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error),
);

checkApiInstanceBackendForDemoCompany.interceptors.response.use(
  (response) => successHandler(response),
  (error) => errorHandler(error),
);

// Check API wrapper
export const checkApi = ({
  url,
  method = 'GET',
  data,
  responseType = 'json',
  isBackend = false,
  isDemoCompany = false,
}: {
  url: string;
  method?: string;
  data?: any;
  isBackend?: boolean;
  isDemoCompany?: boolean;
  responseType?: ResponseType;
}): Promise<CheckApiResponse> => {
  method = method.toUpperCase();
  if (isBackend) {
    if (isDemoCompany) {
      return checkApiInstanceBackendForDemoCompany({ url, method, data, responseType });
    }
    // If requesting CSV, set Accept header explicitly
    const headers =
      responseType === 'text'
        ? { Accept: 'text/csv', Authorization: `Bearer ${process.env.NEXT_PUBLIC_CHECKHQ_API_KEY}` }
        : { Authorization: `Bearer ${process.env.NEXT_PUBLIC_CHECKHQ_API_KEY}` };
    return checkApiInstanceBackend({
      url,
      method,
      data,
      responseType,
      headers,
    });
  }
  return checkApiInstanceFrontend({
    url,
    method,
    data,
    responseType,
  });
};
